/*  omni-search.js — Omnitub owned instant search (MiniSearch, client-side).
 *
 *  A <omni-search> custom element wraps the header search form. On first focus it
 *  loads /assets/omni-search-index.json once, builds a MiniSearch instance with our
 *  OWNED ranking (field boosts + synonyms + prefix + typo-tolerance), and renders
 *  premium grouped results into its own panel — no per-keystroke network call, so
 *  it's instant. Full-listing fallback hands off to Shopify's /search?q= page.
 *
 *  Depends on: window.MiniSearch (minisearch.min.js, loaded before this file).
 */
(function () {
  'use strict';
  if (!window.customElements || window.__omniSearchDefined) return;
  window.__omniSearchDefined = true;

  /* ---- ranking dictionaries (owned) ------------------------------------- */
  // Each token expands to a set of equivalent tokens so "tub" also finds "bath".
  var SYN = {
    tub: ['bath', 'bathtub', 'soaking'], tubs: ['tub', 'bath', 'bathtub'],
    bath: ['tub', 'bathtub', 'soaking'], baths: ['bath', 'tub', 'bathtub'],
    bathtub: ['bath', 'tub'], bathtubs: ['bath', 'tub'],
    soaker: ['soaking', 'tub', 'bath'], soak: ['soaking', 'tub'],
    shower: ['tray'], trays: ['tray', 'shower'], tray: ['shower'],
    basin: ['sink', 'basins'], basins: ['basin', 'sink'], sink: ['basin'],
    waste: ['drain', 'plug', 'wastes', 'omnitone'], wastes: ['waste', 'drain'],
    drain: ['waste'], plug: ['waste'],
    ice: ['plunge', 'cold', 'chill'], plunge: ['ice', 'cold', 'plunge'],
    cold: ['ice', 'plunge'], chill: ['ice', 'plunge'],
    seated: ['seat', 'japanese', 'ofuro'], japanese: ['seated', 'ofuro', 'seat'],
    ofuro: ['japanese', 'seated'], seat: ['seated'],
    colour: ['color', 'ral', 'coloured'], color: ['colour', 'ral'],
    coloured: ['colour', 'ral'], ral: ['colour'],
    delivery: ['shipping', 'deliver', 'dispatch'], shipping: ['delivery', 'deliver'],
    deliver: ['delivery', 'shipping'],
    panel: ['panels', 'surround'], panels: ['panel', 'surround'],
    insulated: ['insulation'], insulation: ['insulated'],
    warranty: ['guarantee'], guarantee: ['warranty'],
    bespoke: ['custom', 'made-to-order'], custom: ['bespoke'],
    price: ['cost'], cost: ['price']
  };
  // Words that express INTENT, not content — never required for a match (so
  // "tub sizes" still returns every tub instead of nothing).
  var INTENT = wordset('size sizes sized dimension dimensions measurement measurements ' +
    'price prices priced cost costs costing buy buying purchase order ' +
    'option options available availability range ranges mm cm');
  var STOP = wordset('the a an of for to in on at and or my your with is it are be ' +
    'what how do does i me can any all some best new get show find search looking need');

  function wordset(s) { var o = {}; s.split(' ').forEach(function (w) { o[w] = 1; }); return o; }

  // Default quick-links shown before the shopper types (premium "browse" state).
  var QUICKLINKS = [
    { label: 'Bathtubs', url: '/collections/bathtubs' },
    { label: 'Shower trays', url: '/collections/shower-trays' },
    { label: 'Ice & plunge baths', url: '/pages/bespoke-ice-baths' },
    { label: 'Colour-matched wastes', url: '/pages/omnitone' },
    { label: 'Design in any colour — OmniStudio', url: '/pages/the-drawing-room' }
  ];
  // Drawing Room hidden (Theme settings → Omnitub): drop its quick link.
  // …unless it's its own destination (Theme settings → "Show the Drawing Room as its own destination").
  if (window.OMN_HIDE_DR && !window.OMN_DR_ENTRY) QUICKLINKS = QUICKLINKS.filter(function (q) { return q.url.indexOf('the-drawing-room') === -1; });
  var POPULAR = ['1700 x 800', 'seated tub', 'shower tray', 'ice bath', 'colour', 'wastes'];

  var TYPE_ORDER = ['product', 'collection', 'page', 'faq', 'article'];
  var TYPE_LABEL = { product: 'Products', collection: 'Collections', page: 'Pages', faq: 'Questions & answers', article: 'Guides' };
  var TYPE_CAP = { product: 6, collection: 4, page: 5, faq: 5, article: 4 };

  function debounce(fn, ms) {
    var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); };
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  class OmniSearch extends HTMLElement {
    constructor() { super(); this.mini = null; this.loading = null; this.open = false; this.active = -1; this.items = []; }

    connectedCallback() {
      this.input = this.querySelector('.js-search-input');
      this.panel = this.querySelector('.omni-results');
      this.form = this.querySelector('form');
      this.indexUrl = this.getAttribute('data-index-url');
      if (!this.input || !this.panel) return;

      this.onInput = debounce(this.run.bind(this), 90);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('keydown', this.onKey.bind(this));
      this.form && this.form.addEventListener('submit', this.onSubmit.bind(this));
      // Keep focus while mousing inside the panel — but ONLY when the press is not on a link/button.
      // preventDefault on the tap-synthesised mousedown CANCELS the click on mobile (results needed a
      // long-press to open — operator bug 21 Jul 2026). Links keep their default so taps navigate.
      this.panel.addEventListener('mousedown', function (e) { if (!e.target.closest('a,button')) e.preventDefault(); });
      document.addEventListener('click', this.onDocClick.bind(this));

      // Full-page results mode: when this element drives a /search page container,
      // render the SAME grouped, owned-ranked results into it on load (read ?q).
      // The dropdown still works on focus/type — one element, two render targets.
      this.pageTarget = this.getAttribute('data-page-target');
      if (this.pageTarget) {
        this.pageEl = document.querySelector(this.pageTarget);
        if (this.pageEl) {
          var self = this, p = this.ensureIndex();
          if (p && p.then) p.then(function () { self.renderPage(); });
          else this.renderPage();
        }
      }
    }

    onFocus() { this.ensureIndex(); if (this.input.value.trim()) this.run(); else this.renderDefault(); }

    onDocClick(e) { if (this.open && !this.contains(e.target)) this.close(); }

    ensureIndex() {
      if (this.mini || this.loading) return this.loading;
      var self = this;
      this.loading = fetch(this.indexUrl, { credentials: 'omit' })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var mini = new window.MiniSearch({
            fields: ['title', 'sizes', 'ptype', 'tags', 'context', 'body'],
            storeFields: ['type', 'title', 'url', 'image', 'price', 'ptype', 'context', 'boost'],
            searchOptions: {
              prefix: true, fuzzy: 0.2, combineWith: 'AND',
              boost: { title: 6, sizes: 5, ptype: 3, context: 2, tags: 2, body: 1 }
            }
          });
          // accessory-visibility 2026-09-23: add-on/component products carry the 'hidden' tag — purchasable, never search-listed.
          mini.addAll((data.docs || []).filter(function (d) { return !(d.type === 'product' && /(^| )hidden( |$)/.test(d.tags || '')) && !(window.OMN_HIDE_DR && !window.OMN_DR_ENTRY && /the-drawing-room/.test(d.url || '')); }));
          self.mini = mini;
          if (self.input.value.trim()) self.run();
          return mini;
        })
        .catch(function () { self.loading = null; });
      return this.loading;
    }

    // Turn the raw query into {content:[tokens], intent:bool, raw}
    parse(q) {
      var toks = q.toLowerCase().split(/[\s,\/]+/).filter(Boolean);
      var content = [], intent = false, leftovers = [];
      toks.forEach(function (t) {
        if (t.length < 2) return;
        if (STOP[t]) return;
        if (INTENT[t]) { intent = true; return; }
        content.push(t); leftovers.push(t);
      });
      if (!content.length) content = leftovers.length ? leftovers
        : toks.filter(function (t) { return t.length >= 2 && !STOP[t]; });
      return { content: content, intent: intent };
    }

    buildQuery(content) {
      // AND across content tokens; each token ORs in its synonyms.
      return {
        combineWith: 'AND',
        queries: content.map(function (t) {
          // Numeric size tokens (e.g. 1100, 1700) match EXACTLY — no fuzzy/prefix.
          // Otherwise "1100" drags in 1000/1200/1150/1600 (one edit away), which
          // is exactly the noise customers complained about on a size search.
          if (/^\d{3,4}$/.test(t)) return { queries: [t], fuzzy: false, prefix: false };
          var syn = SYN[t] || [];
          return syn.length ? { combineWith: 'OR', queries: [t].concat(syn) } : t;
        })
      };
    }

    // Shared search used by both the dropdown and the full /search page so ranking
    // is identical everywhere.
    searchRaw(q) {
      if (!this.mini) return [];
      var p = this.parse(q);
      if (!p.content.length) return [];
      var sizeIntent = p.intent, hasNum = /\d{3,4}/.test(q);
      return this.mini.search(this.buildQuery(p.content), {
        boostDocument: function (id, term, stored) {
          var b = stored.boost || 1;
          if ((sizeIntent || hasNum) && stored.type === 'product') b *= 1.4;
          return b;
        }
      });
    }

    run() {
      var q = this.input.value.trim();
      if (!q) { this.renderDefault(); return; }
      if (!this.mini) { this.ensureIndex(); this.renderLoading(); return; }
      if (!this.parse(q).content.length) { this.renderDefault(); return; }
      this.render(this.searchRaw(q).slice(0, 40), q);
    }

    render(results, q) {
      if (!results.length) {
        this.panel.innerHTML = '<div class="omni-empty">No matches for <strong>' + esc(q) +
          '</strong>.<div class="omni-empty__sub">Try a size (e.g. 1700 x 800), “seated tub”, “shower tray” or “colour”.</div></div>';
        return this.show();
      }
      var groups = {}; TYPE_ORDER.forEach(function (t) { groups[t] = []; });
      results.forEach(function (r) { if (groups[r.type] && groups[r.type].length < TYPE_CAP[r.type]) groups[r.type].push(r); });

      var html = '', self = this, tokens = q.toLowerCase().split(/[\s,]+/).filter(function (t) { return t.length >= 2; });
      TYPE_ORDER.forEach(function (type) {
        var rows = groups[type]; if (!rows.length) return;
        html += '<div class="omni-group"><div class="omni-group__head">' + TYPE_LABEL[type] + '</div><ul class="omni-list omni-list--' + type + '">';
        rows.forEach(function (r) { html += self.row(r, type, tokens); });
        html += '</ul></div>';
      });
      html += '<a class="omni-all" href="/search?q=' + encodeURIComponent(q) + '&type=product,page,article">' +
        'See all results for <strong>' + esc(q) + '</strong><span class="omni-all__arr" aria-hidden="true">→</span></a>';
      this.panel.innerHTML = html;
      this.items = Array.prototype.slice.call(this.panel.querySelectorAll('[data-omni-item]'));
      this.active = -1;
      this.show();
    }

    row(r, type, tokens) {
      var media = '';
      if (type === 'product' || type === 'collection') {
        media = r.image
          ? '<span class="omni-row__img"><img src="' + esc(r.image) + '&width=120" alt="" loading="lazy" width="56" height="56" onerror="this.parentNode.classList.add(\'omni-row__img--ph\');this.remove()"></span>'
          : '<span class="omni-row__img omni-row__img--ph" aria-hidden="true"></span>';
      } else {
        var glyph = { page: '◆', faq: '?', article: '✦' }[type] || '◆';
        media = '<span class="omni-row__glyph" aria-hidden="true">' + glyph + '</span>';
      }
      var meta = '';
      if (type === 'product') meta = '<span class="omni-row__meta">' + (r.ptype ? esc(r.ptype) : '') + (r.price ? '<span class="omni-row__price">' + esc(r.price) + '</span>' : '') + '</span>';
      else if (type === 'collection') meta = '<span class="omni-row__meta">Collection</span>';
      else if (type === 'faq' && r.context) meta = '<span class="omni-row__meta">' + esc(r.context) + '</span>';
      return '<li><a class="omni-row omni-row--' + type + '" data-omni-item href="' + esc(r.url) + '">' +
        media + '<span class="omni-row__txt"><span class="omni-row__title">' + hilite(r.title, tokens) + '</span>' + meta + '</span></a></li>';
    }

    // ── Full /search page results (not a dropdown) ────────────────────────
    pageQuery() { try { return (new URLSearchParams(location.search).get('q') || '').trim(); } catch (e) { return ''; } }

    renderPage() {
      if (!this.pageEl) return;
      var q = this.pageQuery();
      if (!q) { this.pageEl.innerHTML = ''; return; }
      if (!this.mini) { this.pageEl.innerHTML = '<div class="omni-loading">Searching…</div>'; return; }
      var results = this.searchRaw(q);
      if (!results.length) {
        this.pageEl.innerHTML = '<div class="omni-empty">No matches for <strong>' + esc(q) +
          '</strong>.<div class="omni-empty__sub">Try a size (e.g. 1700 x 800), “seated tub”, “shower tray” or “colour”.</div></div>';
        this.setCount(0); return;
      }
      var CAP = { product: 60, collection: 12, page: 12, faq: 8, article: 8 };
      var groups = {}; TYPE_ORDER.forEach(function (t) { groups[t] = []; });
      results.forEach(function (r) { if (groups[r.type] && groups[r.type].length < CAP[r.type]) groups[r.type].push(r); });

      var html = '', self = this, total = 0,
          tokens = q.toLowerCase().split(/[\s,]+/).filter(function (t) { return t.length >= 2; });
      TYPE_ORDER.forEach(function (type) {
        var rows = groups[type]; if (!rows.length) return;
        total += rows.length;
        html += '<section class="omni-pgroup omni-pgroup--' + type + '"><h2 class="omni-pgroup__head">' +
          TYPE_LABEL[type] + ' <span class="omni-pgroup__n">' + rows.length + '</span></h2>' +
          '<ul class="omni-list omni-plist omni-plist--' + type + '">';
        rows.forEach(function (r) { html += self.row(r, type, tokens); });
        html += '</ul></section>';
      });
      this.pageEl.innerHTML = html;
      this.setCount(total);
    }

    setCount(n) {
      var c = document.querySelector('[data-omni-count]');
      if (c) c.textContent = n;
      var noun = document.querySelector('[data-omni-noun]');
      if (noun) noun.textContent = (n === 1 ? 'result' : 'results');
    }

    renderDefault() {
      var q = QUICKLINKS.map(function (l) { return '<li><a class="omni-chip" href="' + l.url + '">' + esc(l.label) + '</a></li>'; }).join('');
      var pop = POPULAR.map(function (t) { return '<button type="button" class="omni-pop" data-omni-pop>' + esc(t) + '</button>'; }).join('');
      this.panel.innerHTML =
        '<div class="omni-group"><div class="omni-group__head">Explore</div><ul class="omni-chips">' + q + '</ul></div>' +
        '<div class="omni-group"><div class="omni-group__head">Popular searches</div><div class="omni-pops">' + pop + '</div></div>';
      var self = this;
      Array.prototype.forEach.call(this.panel.querySelectorAll('[data-omni-pop]'), function (b) {
        b.addEventListener('click', function () { self.input.value = b.textContent; self.input.focus(); self.run(); });
      });
      this.items = []; this.active = -1;
      this.show();
    }

    renderLoading() { this.panel.innerHTML = '<div class="omni-loading">Searching…</div>'; this.show(); }

    show() { if (!this.open) { this.open = true; document.documentElement.classList.add('omni-search-open'); } this.panel.hidden = false; this.setAttribute('data-open', ''); }
    close() { this.open = false; this.panel.hidden = true; this.removeAttribute('data-open'); document.documentElement.classList.remove('omni-search-open'); this.active = -1; }

    onSubmit(e) {
      // Let the form submit to Shopify /search for the full listing, but if an
      // item is keyboard-active, follow that instead.
      if (this.active >= 0 && this.items[this.active]) { e.preventDefault(); window.location.href = this.items[this.active].getAttribute('href'); }
    }

    onKey(e) {
      if (e.key === 'Escape') { this.close(); this.input.blur(); return; }
      if (!this.open || !this.items.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.active += (e.key === 'ArrowDown' ? 1 : -1);
        if (this.active < 0) this.active = this.items.length - 1;
        if (this.active >= this.items.length) this.active = 0;
        this.items.forEach(function (el, i) { el.classList.toggle('is-active', i === this.active); }, this);
        var el = this.items[this.active]; if (el) el.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  function hilite(text, tokens) {
    var out = esc(text);
    tokens.forEach(function (t) {
      if (t.length < 2) return;
      // Word-start matches only (Wave 2, 25 Sep 2026): "tub" must not split the brand name as "Omni[tub]".
      var re = new RegExp('(^|[^A-Za-z0-9&;#])(' + t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig');
      out = out.replace(re, '$1<mark>$2</mark>');
    });
    return out;
  }

  customElements.define('omni-search', OmniSearch);
})();
